import _ from 'lodash';
import { FC, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { FORM_ERROR_MAPPING } from 'src/constants';
import { useAIForm } from 'src/contexts';

import { FormGroupProps } from './FormGroup';
import FileUploader from './FileUploader';

export interface FileUploadFieldProps extends Pick<FormGroupProps, 'onUploadFile'> {
  acceptFileTypes?: string;
  label?: string;
  path: string;
  readOnly?: boolean;
  required?: boolean;
  uploadButton?: string;
}

const FileUploadField: FC<FileUploadFieldProps> = (props) => {
  const { path, required, onUploadFile } = props;
  // NOTES: This is difficult to understand where the data for this comes from but
  // this ultimately comes from the AIFormProvider wrapper around the entire form component
  // which is retrieved from the location state that is sent on navigation.
  const { data } = useAIForm();

  const {
    formState: { errors },
    clearErrors,
    register,
    setValue,
    trigger,
    unregister,
  } = useFormContext();

  const hasError = path ? Boolean(_.get(errors, path)) : false;
  const errorType = _.get(errors, `${path}.type`) as unknown as string;
  const errorMessage = errorType ? FORM_ERROR_MAPPING[errorType] : undefined;

  const materialsList = useMemo(() => {
    return data?.materials?.filter((each) => each.fieldPath === path) ?? [];
  }, [data?.materials, path]);

  useEffect(() => {
    if (!path) return;
    register(path, { required });
    setValue(path, materialsList.map((each) => each.fileName).join(','));

    return () => {
      unregister(path);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, unregister, path, required]);

  const handleUploadFile: FileUploadFieldProps['onUploadFile'] = async (file: File, description: string) => {
    const fileS3Key = (await onUploadFile?.(file, description, path)) ?? null;
    if (!path) {
      return fileS3Key;
    }

    setValue(path, fileS3Key);
    if (fileS3Key) {
      clearErrors(path);
    } else {
      trigger(path);
    }
    return fileS3Key;
  };

  return (
    <FileUploader
      {...props}
      data={data}
      errorMessage={errorMessage}
      hasError={hasError}
      onUploadFile={handleUploadFile}
    />
  );
};

export default FileUploadField;
